// src/features/faq/components/BirthdayFAQs.tsx

import React from 'react';
import { Cake, Crown, Settings, ArrowRight, Gift } from 'lucide-react'; // Iconos necesarios

export interface BirthdayFAQItem {
  id: string;
  question: string;
  answer: React.ReactNode;
}

interface BirthdayFAQData {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  faqs: BirthdayFAQItem[];
}

export const BirthdayFAQs = (navigateCallback: (path: string) => void): BirthdayFAQData => {

  return {
    id: 'birthdays',
    title: 'Cumpleaños',
    icon: <Cake className="h-5 w-5 text-pink-500" />,
    description: 'Gestión de cumpleaños de clientes y envío de felicitaciones personalizadas.',
    faqs: [
      {
        id: 'birthdays-access-pro',
        question: '¿Necesito un plan especial para acceder al Panel de Cumpleaños?',
        answer: (
          <div className="bg-amber-50 dark:bg-amber-500/10 p-3 rounded-md border border-amber-200 dark:border-amber-500/30">
            <p className="text-sm text-amber-700">
              <strong className="flex items-center mb-1">
                <Crown className="h-4 w-4 mr-1.5 text-yellow-600 dark:text-yellow-400" />
                Acceso Exclusivo PRO:
              </strong>
              Sí, el Panel de Cumpleaños es una característica premium disponible exclusivamente para nuestros usuarios con <strong>suscripción PRO</strong>.
              Esta herramienta te permite visualizar los cumpleaños de tus clientes, enviarles felicitaciones personalizadas y fortalecer significativamente su lealtad.
              <br />
              Si aún no eres PRO, te invitamos a mejorar tu plan para desbloquear esta y muchas otras funcionalidades avanzadas de fidelización.
              <button 
                onClick={() => navigateCallback('/dashboard/subscription')}
                className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                Actualizar a PRO <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </button>
            </p>
          </div>
        )
      },
      {
        id: 'birthdays-purpose',
        question: '¿Para qué sirve la página de Cumpleaños?',
        answer: (
          <div>
            <p>La página de Cumpleaños (disponible en el plan PRO) te permite:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li>Visualizar fácilmente qué clientes cumplen años en el día actual.</li>
              <li>Ver una lista de los próximos cumpleaños en los siguientes 30 días, ayudándote a planificar.</li>
              <li>Enviar felicitaciones por WhatsApp con un solo clic, desde el número de tu negocio (de forma manual o automática).</li>
              <li>Fomentar la lealtad y crear una conexión más personal con tus clientes mediante este gesto especial.</li>
            </ul>
            <p className="mt-2 text-content-2">Recordar los cumpleaños de tus clientes y enviarles un saludo puede marcar una gran diferencia en su percepción de tu negocio.</p>
            <div className="mt-2">
              <button onClick={() => navigateCallback('/dashboard/birthday-panel')} className="inline-flex items-center text-sm text-accent hover:text-blue-800 dark:text-blue-300">
                <Gift className="h-4 w-4 mr-1.5" />
                Ir al Panel de Cumpleaños
              </button>
            </div>
          </div>
        )
      },
      {
        id: 'birthdays-list',
        question: '¿Qué información se muestra en la lista de cumpleaños?',
        answer: (
          <div>
            <p>En la lista de cumpleaños verás la siguiente información clave de cada cliente:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Nombre del cliente:</strong> Para identificarlo claramente.</li>
              <li><strong>Información de contacto:</strong> Principalmente su número de teléfono para el envío de WhatsApp.</li>
              <li><strong>Puntos acumulados:</strong> Para que tengas contexto de su nivel de fidelidad.</li>
              <li><strong>Fecha de cumpleaños:</strong> El día y mes de su nacimiento.</li>
            </ul>
            <p className="mt-2 text-content-2">La página organiza a los clientes en dos secciones principales:</p>
            <ul className="list-disc ml-5 mt-1">
              <li><strong>Cumpleaños de Hoy:</strong> Clientes que celebran su día en la fecha actual.</li>
              <li><strong>Próximos Cumpleaños:</strong> Una lista de clientes que cumplirán años en los próximos 30 días, agrupados por fecha para facilitar la visualización y planificación.</li>
            </ul>
          </div>
        )
      },
      {
        id: 'birthdays-whatsapp',
        question: '¿Cómo envío un mensaje de felicitación por WhatsApp?',
        answer: (
          <div>
            <p>Felicitar es muy sencillo y el mensaje se envía <strong>desde el WhatsApp vinculado de tu negocio</strong> (no se abre WhatsApp en tu teléfono):</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>Navega a la página de <button onClick={() => navigateCallback('/dashboard/birthday-panel')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Cumpleaños</button> desde el menú (disponible en el plan PRO).</li>
              <li>Asegúrate de tener tu <strong>WhatsApp conectado</strong>; si no, el panel te mostrará un botón para vincularlo.</li>
              <li>Localiza al cliente que deseas felicitar, ya sea en "Cumpleaños de Hoy" o en "Próximos Cumpleaños".</li>
              <li>Haz clic en el botón <strong>"Felicitar"</strong>. El sistema envía la felicitación al instante usando tu plantilla configurada.</li>
            </ol>
            <p className="mt-2 text-content-2">
              Cada cliente se felicita <strong>una sola vez al año</strong>: si ya recibió su saludo, el botón aparecerá como "Felicitado" para evitar mensajes duplicados. El mensaje utiliza una plantilla que puedes configurar (consulta la pregunta sobre personalización).
            </p>
          </div>
        )
      },
      {
        id: 'birthdays-automatic',
        question: '¿Puedo enviar las felicitaciones automáticamente?',
        answer: (
          <div>
            <p>Sí. Además del envío manual con el botón "Felicitar", el Panel de Cumpleaños incluye un <strong>modo automático</strong>:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Envío automático de cumpleaños:</strong> activa el interruptor y el sistema felicitará por ti cada mañana a los clientes que cumplen años ese día, sin que tengas que entrar al panel.</li>
              <li><strong>Goteo cuidado (anti-bloqueo):</strong> los mensajes se envían espaciados a lo largo de la mañana para proteger tu número de WhatsApp.</li>
              <li><strong>Resumen diario al dueño:</strong> opcionalmente recibes por WhatsApp un resumen de a quién se felicitó ese día.</li>
              <li><strong>Imagen de felicitación:</strong> puedes adjuntar una imagen que acompañe el saludo.</li>
            </ul>
            <p className="mt-2 text-content-2">El envío automático usa la misma plantilla y respeta el límite de una felicitación por cliente al año.</p>
          </div>
        )
      },
      {
        id: 'birthdays-customize-template',
        question: '¿Cómo puedo cambiar el mensaje de felicitación predeterminado?',
        answer: (
          <div>
            <p>Puedes personalizar la plantilla global que se utiliza para los mensajes de cumpleaños por WhatsApp. Sigue estos pasos:</p>
            <ol className="list-decimal ml-5 mt-2 space-y-1">
              <li>En la parte superior de la página de <button onClick={() => navigateCallback('/dashboard/birthday-panel')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">Cumpleaños</button>, busca y haz clic en el botón que dice <span className="inline-flex items-center font-medium"><Settings className="h-4 w-4 mr-1.5 text-content-2"/>Personalizar Mensaje</span>.</li>
              <li>Se abrirá un editor donde podrás modificar el texto base del mensaje.</li>
              <li>Puedes usar variables especiales: <code>@nombre</code> (nombre del cliente), <code>@marca</code> o <code>@negocio</code> (el nombre de tu negocio) y <code>@puntos</code> (sus puntos acumulados). Estas se reemplazan automáticamente con la información de cada cliente.</li>
              <li>Guarda los cambios en la plantilla.</li>
            </ol>
            {/* La Nota PRO de aquí ha sido eliminada según la solicitud */}
          </div>
        )
      },
      // La pregunta 'birthdays-data-source' ha sido eliminada según la solicitud
      {
        id: 'birthdays-no-data',
        question: '¿Qué hago si no veo ningún cumpleaños en la lista?',
        answer: (
          <div>
            <p>Si no visualizas cumpleaños en el panel, podría deberse a una o varias de las siguientes razones:</p>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><strong>Sin Coincidencias:</strong> Simplemente no hay clientes registrados cuya fecha de cumpleaños coincida con el día de hoy o los próximos 30 días.</li>
              <li><strong>Datos Faltantes:</strong> Es posible que no hayas registrado aún las fechas de cumpleaños de tus clientes. Te recomendamos verificar los perfiles y añadir esta información para aprovechar al máximo esta función.</li>
              <li><strong>Acceso al Plan:</strong> Recuerda que el Panel de Cumpleaños es una característica del plan PRO. Asegúrate de que tu suscripción esté activa. Puedes revisar el estado de tu plan <button onClick={() => navigateCallback('/dashboard/subscription')} className="text-accent hover:text-blue-800 dark:text-blue-300 font-medium">aquí</button>.</li>
            </ul>
            <p className="mt-2 text-content-2">Asegúrate de tener una suscripción PRO activa y de haber ingresado las fechas de nacimiento de tus clientes para verlos listados aquí.</p>
          </div>
        )
      },
    ]
  };
};

// export default BirthdayFAQs; // Opcional